// CourseCustomFormBuilder.tsx

import { useMemo } from 'react';
import { Center, Loader } from '@mantine/core';
import { useCourseCustomFieldService } from '@actschurch/ccms-sdk';

import {
  FormBuilder,
  createCustomFieldAdapter,
  useFieldTypeCatalogue,
} from '@/components/advanced/FormBuilder';

// ---------------------------------------------------------------------------
// Course custom form page
// ---------------------------------------------------------------------------

export interface CourseFormPageProps {
  courseID: number;
  /** Called when the builder's Done button is pressed. Usually closes the hosting modal. */
  onDone?: () => void;
}

export function CourseFormPage({ courseID, onDone }: CourseFormPageProps) {
  const courseCustomField = useCourseCustomFieldService();
  const fieldTypes = useFieldTypeCatalogue();

  const adapter = useMemo(() => {
    if (!fieldTypes) return null;
    return createCustomFieldAdapter({
      fieldTypes,
      list: () => courseCustomField.byCourse(courseID),
      create: (data) => courseCustomField.create({ ...data, courseID }),
      update: (id, data) => courseCustomField.update(id, data),
      remove: (id) => courseCustomField.delete(id),
    });
  }, [fieldTypes, courseID, courseCustomField]);

  if (!adapter || !fieldTypes) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <FormBuilder
      title="Course Registration Custom Form"
      subtitle="Applies to all registrations for this Course"
      adapter={adapter}
      formTypes={fieldTypes}
      onDone={onDone}
    />
  );
}

// ---------------------------------------------------------------------------
// Read-only viewer — same FormBuilder component, same adapter shape, just
// `readonly` so there's no field palette, no edit/delete/move controls, and
// no Save button. Only needs `list`, since nothing here ever writes.
// ---------------------------------------------------------------------------

export interface CourseFormViewerProps {
  courseID: number;
}

export function CourseFormViewer({ courseID }: CourseFormViewerProps) {
  const courseCustomField = useCourseCustomFieldService();
  const fieldTypes = useFieldTypeCatalogue();

  const adapter = useMemo(() => {
    if (!fieldTypes) return null;
    return createCustomFieldAdapter({
      fieldTypes,
      list: () => courseCustomField.byCourse(courseID),
    });
  }, [fieldTypes, courseID, courseCustomField]);

  if (!adapter || !fieldTypes) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <FormBuilder
      title="Course Registration Custom Form"
      subtitle="Read-only view"
      adapter={adapter}
      formTypes={fieldTypes}
      readonly
    />
  );
}


export default CourseFormPage;